import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { NodeSelection } from '@tiptap/pm/state';
import { Editor } from '@tiptap/react';

import { useReportComponentStore } from '@/stores';
import { ResourceComment } from '@/types';
import { dayjs } from '@/utilities';

import { MemoCommentHighlightRange } from '../extensions/MemoCommentHighlight';

export type MemoCommentTarget = {
    component: string;
    label: string;
    metadata: Record<string, unknown>;
    quote?: string;
};

export type MemoCommentHoverCardPosition = {
    left: number;
    maxHeight: number;
    top: number;
    width: number;
};

export type MemoCommentHoverTarget = {
    anchor: HTMLElement;
    commentId: string;
};

const COMMENT_HOVER_CARD_DEFAULT_WIDTH = 340;
const COMMENT_HOVER_CARD_LEFT_RAIL_GUTTER = 48;
const COMMENT_HOVER_CARD_MIN_HEIGHT = 180;
const COMMENT_HOVER_CARD_MIN_WIDTH = 260;
const COMMENT_HOVER_CARD_VIEWPORT_GUTTER = 12;
const COMMENT_HOVER_CARD_RIGHT_GAP = 24;
const REPORT_COMPONENT_COMMENT_PREFIX = 'reportComponent:';
const NON_CONTEXT_ATOM_NODE_NAMES = new Set(['hardBreak']);
const MEMO_COMMENT_TARGET_SELECTOR = [
    '[data-memo-comment-id]',
    '[data-memo-report-component-id]',
    '[data-report-component-embed]',
    '[data-type]',
    '.callout',
    'p',
    'li',
    'blockquote',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
].join(', ');

const commentableNodeLabels: Record<string, string> = {
    blockquote: 'Blockquote',
    bulletList: 'Bulleted List',
    callout: 'Callout',
    codeBlock: 'Code Block',
    iframe: 'Embed',
    image: 'Image',
    linkEmbed: 'Link Embed',
    orderedList: 'Numbered List',
    paragraph: 'Paragraph',
    reportComponentEmbed: 'Embedded Report Component',
    table: 'Table',
    taskList: 'Checklist',
    unfurledLink: 'Link Preview',
    youtube: 'YouTube Embed',
};

const normalizeCommentText = (value: string) =>
    value
        .replace(/[\u200B-\u200D\u2060\uFEFF]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

const truncate = (value: string, maxLength = 180) => {
    const normalized = normalizeCommentText(value);

    return normalized.length > maxLength ? `${normalized.slice(0, maxLength - 1)}...` : normalized;
};

const componentKey = (value: string) => value.replace(/[^a-zA-Z0-9:._-]/g, '-').slice(0, 120);

const nodeAttrs = (attrs: Record<string, unknown>) =>
    Object.fromEntries(Object.entries(attrs ?? {}).filter(([, value]) => value !== null && value !== undefined));

const stringValue = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : undefined);

const isMeaningfulAtom = (node: ProseMirrorNode) =>
    node.isAtom && !node.isText && !NON_CONTEXT_ATOM_NODE_NAMES.has(node.type.name);

export const hasMeaningfulMemoCommentContext = (node: ProseMirrorNode) => {
    if (node.type.name === 'table' || normalizeCommentText(node.textContent).length > 0 || isMeaningfulAtom(node)) {
        return true;
    }

    let hasMeaningfulDescendant = false;

    node.descendants((child) => {
        if (normalizeCommentText(child.textContent).length > 0 || isMeaningfulAtom(child)) {
            hasMeaningfulDescendant = true;
            return false;
        }

        return true;
    });

    return hasMeaningfulDescendant;
};

const reportComponentForAttrs = (attrs: Record<string, unknown>) => {
    const reportComponentId = stringValue(attrs.reportComponentId);

    return reportComponentId ? useReportComponentStore.getState().reportComponentsById[reportComponentId] : undefined;
};

const getCommentableNodeTarget = (node: ProseMirrorNode, position: number) => {
    const attrs = nodeAttrs(node.attrs);
    const reportComponent = reportComponentForAttrs(attrs);
    const reportComponentId = stringValue(attrs.reportComponentId);
    const highlightRange =
        node.isTextblock && node.content.size > 0
            ? {
                  from: position + 1,
                  to: position + node.nodeSize - 1,
              }
            : undefined;
    const headingLabel =
        node.type.name === 'heading' && typeof attrs.level === 'number' ? `Heading ${attrs.level}` : undefined;
    const label =
        reportComponent?.name ??
        stringValue(attrs.name) ??
        stringValue(attrs.title) ??
        headingLabel ??
        commentableNodeLabels[node.type.name] ??
        node.type.name;

    return {
        attrs,
        componentType: reportComponent?.type,
        from: highlightRange?.from,
        label,
        nodeType: node.type.name,
        position,
        quote: truncate(node.textContent ?? '', 120),
        reportComponentId,
        reportComponentName: reportComponent?.name,
        to: highlightRange?.to,
    };
};

export const numericTargetValue = (target: Record<string, unknown>, key: string) => {
    const value = target[key];

    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

export const metadataTarget = (comment: ResourceComment) => (comment.metadata?.target ?? {}) as Record<string, unknown>;

const metadataTargetAttrs = (comment: ResourceComment) =>
    (metadataTarget(comment).attrs ?? {}) as Record<string, unknown>;

export const reportComponentIdFromComment = (comment: ResourceComment) => {
    const target = metadataTarget(comment);
    const attrs = metadataTargetAttrs(comment);

    if (typeof target.componentId === 'string') {
        return target.componentId;
    }

    if (typeof target.reportComponentId === 'string') {
        return target.reportComponentId;
    }

    if (typeof attrs.reportComponentId === 'string') {
        return attrs.reportComponentId;
    }

    if (comment.component?.startsWith(REPORT_COMPONENT_COMMENT_PREFIX)) {
        return comment.component.slice(REPORT_COMPONENT_COMMENT_PREFIX.length);
    }

    return undefined;
};

const HTMLElementFromDomNode = (node: globalThis.Node | null | undefined) => {
    if (node instanceof HTMLElement) {
        return node;
    }

    return node instanceof Text ? node.parentElement : undefined;
};

export const getMemoCommentHoverElementFromEventTarget = (target: EventTarget | null): HTMLElement | undefined => {
    if (target instanceof HTMLElement) {
        return target;
    }

    if (target instanceof Text) {
        return target.parentElement ?? undefined;
    }

    if (!(target instanceof Element)) {
        return undefined;
    }

    return target.closest<HTMLElement>(MEMO_COMMENT_TARGET_SELECTOR) ?? target.parentElement ?? undefined;
};

export const getEditorSelectionState = (editor: Editor) => {
    const { selection, doc } = editor.state;
    const selectedNode = selection instanceof NodeSelection;
    const selectedText = normalizeCommentText(doc.textBetween(selection.from, selection.to, ' '));

    return {
        canUseInlineControls: !selectedNode || Boolean(selectedText),
        hasSelectedText: !selection.empty && !selectedNode && Boolean(selectedText),
    };
};

const getClosestCommentableNode = (editor: Editor) => {
    const { selection } = editor.state;
    const selectedNode = selection instanceof NodeSelection ? selection.node : undefined;

    if (selectedNode && hasMeaningfulMemoCommentContext(selectedNode)) {
        return getCommentableNodeTarget(selectedNode, selection.from);
    }

    for (let depth = selection.$from.depth; depth > 0; depth -= 1) {
        const node = selection.$from.node(depth);
        const label = commentableNodeLabels[node.type.name];

        if (hasMeaningfulMemoCommentContext(node) && (label || node.type.spec.draggable || node.type.spec.selectable)) {
            return getCommentableNodeTarget(node, selection.$from.before(depth));
        }
    }

    return undefined;
};

export const getMemoCommentTargetFromNode = (
    node: ProseMirrorNode,
    position: number,
): MemoCommentTarget | undefined => {
    if (!hasMeaningfulMemoCommentContext(node)) {
        return undefined;
    }

    const target = getCommentableNodeTarget(node, position);
    const component = target.reportComponentId
        ? `reportComponent:${target.reportComponentId}`
        : `${target.nodeType}:${target.position}`;

    return {
        component: componentKey(component),
        label: target.label,
        quote: target.quote,
        metadata: {
            source: 'memo-editor',
            target: {
                attrs: target.attrs,
                ...(target.reportComponentId ? { componentId: target.reportComponentId } : {}),
                ...(target.componentType ? { componentType: target.componentType } : {}),
                ...(target.from !== undefined && target.to !== undefined ? { from: target.from, to: target.to } : {}),
                ...(target.reportComponentName ? { reportComponentName: target.reportComponentName } : {}),
                label: target.label,
                nodeType: target.nodeType,
                position: target.position,
                quote: target.quote,
                route: window.location.pathname,
                type: 'component',
            },
        },
    };
};

export const getMemoCommentTarget = (editor: Editor): MemoCommentTarget | undefined => {
    const { selection, doc } = editor.state;
    const { from, to } = selection;
    const quote = truncate(doc.textBetween(from, to, ' '), 240);
    const closestNode = getClosestCommentableNode(editor);

    if (quote) {
        return {
            component: componentKey(`selection:${from}-${to}`),
            label: closestNode ? `Selection in ${closestNode.label}` : 'Text Selection',
            quote,
            metadata: {
                source: 'memo-editor',
                target: {
                    from,
                    label: closestNode ? `Selection in ${closestNode.label}` : 'Text Selection',
                    node: closestNode,
                    quote,
                    route: window.location.pathname,
                    to,
                    type: 'text-selection',
                },
            },
        };
    }

    if (closestNode) {
        const reportComponentId = closestNode.reportComponentId;

        return {
            component: componentKey(
                reportComponentId
                    ? `reportComponent:${reportComponentId}`
                    : `${closestNode.nodeType}:${closestNode.position}`,
            ),
            label: closestNode.label,
            quote: closestNode.quote,
            metadata: {
                source: 'memo-editor',
                target: {
                    attrs: closestNode.attrs,
                    ...(reportComponentId ? { componentId: reportComponentId } : {}),
                    ...(closestNode.componentType ? { componentType: closestNode.componentType } : {}),
                    ...(closestNode.from !== undefined && closestNode.to !== undefined
                        ? { from: closestNode.from, to: closestNode.to }
                        : {}),
                    ...(closestNode.reportComponentName
                        ? { reportComponentName: closestNode.reportComponentName }
                        : {}),
                    label: closestNode.label,
                    nodeType: closestNode.nodeType,
                    position: closestNode.position,
                    quote: closestNode.quote,
                    route: window.location.pathname,
                    type: 'component',
                },
            },
        };
    }

    return undefined;
};

export const commentHighlightRangeFromComment = (comment: ResourceComment): MemoCommentHighlightRange | undefined => {
    const target = metadataTarget(comment);
    const from = numericTargetValue(target, 'from');
    const to = numericTargetValue(target, 'to');

    if (from === undefined || to === undefined || from >= to) {
        return undefined;
    }

    return {
        commentId: comment.id,
        from,
        to,
    };
};

export const commenterName = (comment: ResourceComment) => {
    const user = comment.user ?? comment.member?.user;
    const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();

    return fullName || user?.emailAddress || 'Unknown';
};

export const upsertComment = (comments: ResourceComment[], comment: ResourceComment) => {
    const exists = comments.some((item) => item.id === comment.id);

    return exists ? comments.map((item) => (item.id === comment.id ? comment : item)) : [comment, ...comments];
};

export const childCommentsFor = (comments: ResourceComment[], parentId: string) =>
    comments
        .filter((comment) => comment.parentId === parentId)
        .sort((first, second) => dayjs(first.createdAt).valueOf() - dayjs(second.createdAt).valueOf());

const getScrollParent = (element?: HTMLElement | null): HTMLElement | Window => {
    let parent = element?.parentElement;

    while (parent) {
        const style = window.getComputedStyle(parent);
        const overflowY = style.overflowY;

        if (/(auto|scroll|overlay)/.test(overflowY)) {
            return parent;
        }

        parent = parent.parentElement;
    }

    return window;
};

const getCommentHighlightAnchorRect = (commentId: string, fallbackHighlight: HTMLElement, editorRoot: HTMLElement) => {
    const firstHighlight = Array.from(editorRoot.querySelectorAll<HTMLElement>('[data-memo-comment-id]')).find(
        (element) => element.dataset.memoCommentId === commentId,
    );
    const firstCommentElement = firstHighlight ?? fallbackHighlight;
    const containingBlock = firstCommentElement.closest<HTMLElement>(
        'p, li, blockquote, h1, h2, h3, h4, h5, h6, [data-memo-report-component-id], [data-type]',
    );

    if (containingBlock && editorRoot.contains(containingBlock)) {
        return containingBlock.getBoundingClientRect();
    }

    return firstCommentElement.getBoundingClientRect();
};

export const getCommentHoverCardPosition = (
    commentId: string,
    highlight: HTMLElement,
    editorRoot: HTMLElement,
): MemoCommentHoverCardPosition => {
    const anchorRect = getCommentHighlightAnchorRect(commentId, highlight, editorRoot);
    const contentColumn = editorRoot.closest<HTMLElement>('[data-memo-content-column="true"]');
    const contentLeft = contentColumn?.getBoundingClientRect().left ?? editorRoot.getBoundingClientRect().left;
    const scrollParent = getScrollParent(contentColumn ?? editorRoot);
    const scrollParentRect =
        scrollParent === window
            ? {
                  bottom: window.innerHeight,
                  left: 0,
                  top: 0,
              }
            : (scrollParent as HTMLElement).getBoundingClientRect();
    const scrollParentLeft = scrollParentRect.left;
    const railLeft = Math.max(
        COMMENT_HOVER_CARD_VIEWPORT_GUTTER,
        scrollParentLeft + COMMENT_HOVER_CARD_LEFT_RAIL_GUTTER,
    );
    const availableRailWidth = contentLeft - railLeft - COMMENT_HOVER_CARD_RIGHT_GAP;
    const viewportAvailableWidth = window.innerWidth - railLeft - COMMENT_HOVER_CARD_VIEWPORT_GUTTER;
    const width =
        availableRailWidth >= COMMENT_HOVER_CARD_MIN_WIDTH
            ? Math.min(COMMENT_HOVER_CARD_DEFAULT_WIDTH, availableRailWidth)
            : Math.min(
                  COMMENT_HOVER_CARD_DEFAULT_WIDTH,
                  Math.max(COMMENT_HOVER_CARD_MIN_WIDTH, viewportAvailableWidth),
              );
    const viewportTop = Math.max(
        COMMENT_HOVER_CARD_VIEWPORT_GUTTER,
        scrollParentRect.top + COMMENT_HOVER_CARD_VIEWPORT_GUTTER,
    );
    const viewportBottom = Math.min(
        window.innerHeight - COMMENT_HOVER_CARD_VIEWPORT_GUTTER,
        scrollParentRect.bottom - COMMENT_HOVER_CARD_VIEWPORT_GUTTER,
    );
    const maxTop = Math.max(viewportTop, viewportBottom - COMMENT_HOVER_CARD_MIN_HEIGHT);
    const top = Math.min(Math.max(anchorRect.top, viewportTop), maxTop);
    const maxHeight = Math.max(0, viewportBottom - top);

    return {
        left: railLeft,
        maxHeight,
        top,
        width,
    };
};

const getReportComponentAnchor = (editorRoot: HTMLElement, reportComponentId: string) =>
    Array.from(editorRoot.querySelectorAll<HTMLElement>('[data-memo-report-component-id]')).find(
        (element) => element.dataset.memoReportComponentId === reportComponentId,
    );

const getComponentAnchorFromComment = (comment: ResourceComment, editor: Editor, editorRoot: HTMLElement) => {
    const reportComponentId = reportComponentIdFromComment(comment);

    if (reportComponentId) {
        return getReportComponentAnchor(editorRoot, reportComponentId);
    }

    const position = numericTargetValue(metadataTarget(comment), 'position');

    if (position === undefined) {
        return undefined;
    }

    const element = HTMLElementFromDomNode(editor.view.nodeDOM(position));

    return element && editorRoot.contains(element) ? element : undefined;
};

export const getCommentHoverTargetForComment = (
    comment: ResourceComment,
    editor: Editor,
    editorRoot: HTMLElement,
): MemoCommentHoverTarget | undefined => {
    const highlight = Array.from(editorRoot.querySelectorAll<HTMLElement>('[data-memo-comment-id]')).find(
        (element) => element.dataset.memoCommentId === comment.id,
    );

    if (highlight) {
        return { anchor: highlight, commentId: comment.id };
    }

    const componentAnchor = getComponentAnchorFromComment(comment, editor, editorRoot);

    return componentAnchor ? { anchor: componentAnchor, commentId: comment.id } : undefined;
};

const findComponentCommentHoverTarget = (
    targetElement: HTMLElement,
    comments: ResourceComment[],
    editor: Editor,
    editorRoot: HTMLElement,
): MemoCommentHoverTarget | undefined => {
    const reportComponentAnchor = targetElement.closest<HTMLElement>('[data-memo-report-component-id]');
    const reportComponentId = reportComponentAnchor?.dataset.memoReportComponentId;
    const topLevelComments = comments.filter((comment) => !comment.parentId);

    if (reportComponentId) {
        const comment = topLevelComments.find((item) => reportComponentIdFromComment(item) === reportComponentId);

        return comment ? { anchor: reportComponentAnchor, commentId: comment.id } : undefined;
    }

    const comment = topLevelComments.find((item) => {
        const target = metadataTarget(item);

        if (target.type !== 'component') {
            return false;
        }

        const anchor = getComponentAnchorFromComment(item, editor, editorRoot);

        return Boolean(anchor && (anchor === targetElement || anchor.contains(targetElement)));
    });

    if (!comment) {
        return undefined;
    }

    const anchor = getComponentAnchorFromComment(comment, editor, editorRoot);

    return anchor ? { anchor, commentId: comment.id } : undefined;
};

export const getCommentHoverTargetFromElement = (
    targetElement: HTMLElement,
    comments: ResourceComment[],
    editor: Editor,
    editorRoot: HTMLElement,
): MemoCommentHoverTarget | undefined => {
    const highlight = targetElement.closest<HTMLElement>('[data-memo-comment-id]');
    const commentId = highlight?.dataset.memoCommentId;

    if (highlight && commentId && comments.some((comment) => comment.id === commentId && !comment.parentId)) {
        return {
            anchor: highlight,
            commentId,
        };
    }

    return findComponentCommentHoverTarget(targetElement, comments, editor, editorRoot);
};
